import { Navbar } from "@/components/layout/Navbar";
import { Footer } from "@/components/layout/Footer";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { AlertTriangle, ChevronDown, ArrowLeft, Calculator } from "lucide-react";
import { useState, useEffect, useRef } from "react";
import { useParams, Link } from "wouter";

const CALC_DISCLAIMER = "The calculators and tools provided on this website are for informational and illustrative purposes only. While Process Design Institute Ltd. makes reasonable efforts to ensure the accuracy of the calculations and underlying formulas, no representation or warranty is made regarding the accuracy, completeness, reliability, or suitability of the results.\n\nThe results generated by these calculators are estimates based on the information provided by users and may not reflect actual financial outcomes. Users should independently verify all calculations and consult appropriate professional advisors before making business, financial, or investment decisions.\n\nProcess Design Institute Ltd., its brands, and its affiliates shall not be liable for any losses, damages, or decisions arising from the use of or reliance on these calculators or the information they produce.";
const MICRO = "Results are estimates for informational purposes only and should not be relied upon for financial or investment decisions.";

function InlineDisclaimer() {
  const [expanded, setExpanded] = useState(false);
  return (
    <div className="mt-6 border-t border-white/5 pt-4">
      <button
        onClick={() => setExpanded(!expanded)}
        className="flex items-center gap-2 text-xs text-muted-foreground hover:text-foreground transition-colors"
        data-testid="button-inline-disclaimer"
        aria-expanded={expanded}
        aria-controls="inline-disclaimer-content"
        aria-label="Calculator Disclaimer"
      >
        <AlertTriangle size={12} aria-hidden="true" />
        <span className="font-medium">Calculator Disclaimer</span>
        <ChevronDown size={12} className={`transition-transform duration-200 ${expanded ? "rotate-180" : ""}`} aria-hidden="true" />
      </button>
      {expanded && (
        <div id="inline-disclaimer-content" className="mt-3 text-xs text-muted-foreground leading-relaxed space-y-2" role="alert">
          {CALC_DISCLAIMER.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
        </div>
      )}
    </div>
  );
}

function DisclaimerModal({ onAccept }: { onAccept: () => void }) {
  const dialogRef = useRef<HTMLDivElement>(null);
  const acceptBtnRef = useRef<HTMLButtonElement>(null);
  const previouslyFocusedRef = useRef<HTMLElement | null>(null);

  useEffect(() => {
    previouslyFocusedRef.current = document.activeElement as HTMLElement;
    acceptBtnRef.current?.focus();
    return () => {
      previouslyFocusedRef.current?.focus();
    };
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") {
        onAccept();
      }
      if (e.key === "Tab" && dialogRef.current) {
        const focusable = dialogRef.current.querySelectorAll<HTMLElement>(
          'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
        );
        if (focusable.length === 0) return;
        const first = focusable[0];
        const last = focusable[focusable.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    document.addEventListener("keydown", handleKeyDown);
    return () => document.removeEventListener("keydown", handleKeyDown);
  }, [onAccept]);

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm p-4" role="alertdialog" aria-modal="true" aria-labelledby="disclaimer-title" aria-describedby="disclaimer-content">
      <div ref={dialogRef} className="bg-card border border-white/10 rounded-2xl shadow-2xl max-w-lg w-full p-8 animate-in fade-in zoom-in-95 duration-200">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center">
            <AlertTriangle size={20} className="text-primary" aria-hidden="true" />
          </div>
          <h2 id="disclaimer-title" className="text-xl font-display font-bold">Disclaimer</h2>
        </div>
        <div id="disclaimer-content" className="text-sm text-muted-foreground leading-relaxed space-y-4 mb-8">
          {CALC_DISCLAIMER.split("\n\n").map((p, i) => <p key={i}>{p}</p>)}
        </div>
        <Button ref={acceptBtnRef} onClick={onAccept} className="w-full font-semibold" data-testid="button-accept-disclaimer">
          I Understand & Accept
        </Button>
      </div>
    </div>
  );
}

function fmt(n: number): string {
  if (isNaN(n) || !isFinite(n)) return "$0";
  return "$" + n.toLocaleString("en-US", { maximumFractionDigits: 0 });
}
function fmtNum(n: number): string {
  if (isNaN(n) || !isFinite(n)) return "0";
  return n.toLocaleString("en-US", { maximumFractionDigits: 1 });
}
function fmtPct(n: number): string {
  if (isNaN(n) || !isFinite(n)) return "0%";
  return n.toFixed(1) + "%";
}

function NumInput({ label, value, onChange, prefix, suffix, placeholder }: { label: string; value: string; onChange: (v: string) => void; prefix?: string; suffix?: string; placeholder?: string }) {
  const testId = `input-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  const inputId = `calc-input-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div className="space-y-1.5">
      <Label htmlFor={inputId} className="text-sm">{label}</Label>
      <div className="relative">
        {prefix && <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm" aria-hidden="true">{prefix}</span>}
        <Input id={inputId} type="number" value={value} onChange={(e) => onChange(e.target.value)} placeholder={placeholder || "0"} aria-label={label} className={`${prefix ? "pl-7" : ""} ${suffix ? "pr-8" : ""}`} data-testid={testId} />
        {suffix && <span className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground text-sm" aria-hidden="true">{suffix}</span>}
      </div>
    </div>
  );
}

function ResultsRegion({ children }: { children: React.ReactNode }) {
  return (
    <div aria-live="polite" aria-atomic="true" role="status">
      {children}
    </div>
  );
}

function Result({ label, value, sub }: { label: string; value: string; sub?: string }) {
  const testId = `result-${label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`;
  return (
    <div className="bg-white/[0.03] rounded-lg p-4" data-testid={testId}>
      <p className="text-xs text-muted-foreground mb-1">{label}</p>
      <p className="text-xl font-display font-bold text-primary">{value}</p>
      {sub && <p className="text-xs text-muted-foreground mt-1">{sub}</p>}
      <p className="text-[10px] text-muted-foreground/60 mt-2 leading-snug">{MICRO}</p>
    </div>
  );
}

function FAQ({ items }: { items: { q: string; a: string }[] }) {
  const [open, setOpen] = useState<number | null>(null);
  return (
    <div className="space-y-2" role="group" aria-label="Frequently Asked Questions">
      {items.map((item, i) => (
        <div key={i} className="border border-white/5 rounded-lg overflow-hidden">
          <button onClick={() => setOpen(open === i ? null : i)} className="w-full flex items-center justify-between p-4 text-left hover:bg-white/[0.02] transition-colors" data-testid={`button-faq-${i}`} aria-expanded={open === i} aria-controls={`faq-answer-${i}`}>
            <span className="font-medium text-sm pr-4">{item.q}</span>
            <ChevronDown size={16} className={`text-muted-foreground shrink-0 transition-transform duration-200 ${open === i ? "rotate-180" : ""}`} aria-hidden="true" />
          </button>
          {open === i && <div id={`faq-answer-${i}`} className="px-4 pb-4 text-sm text-muted-foreground leading-relaxed">{item.a}</div>}
        </div>
      ))}
    </div>
  );
}

function ExampleCard({ title, description }: { title: string; description: string }) {
  return (
    <div className="bg-white/[0.02] border border-white/5 rounded-lg p-5">
      <h4 className="font-display font-semibold text-sm mb-2">{title}</h4>
      <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
    </div>
  );
}

// ── Calculator Components ──

function RunwayCalc({ show }: { show: boolean }) {
  const [cash, setCash] = useState("");
  const [burn, setBurn] = useState("");
  const months = parseFloat(cash) / parseFloat(burn);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NumInput label="Cash on Hand" value={cash} onChange={setCash} prefix="$" />
        <NumInput label="Monthly Burn Rate" value={burn} onChange={setBurn} prefix="$" />
      </div>
      <ResultsRegion>
        {show && cash && burn && parseFloat(burn) > 0 && (
          <Result label="Estimated Runway" value={`${fmtNum(months)} months`} sub={months < 6 ? "Warning: Less than 6 months of runway" : months >= 18 ? "Healthy runway" : "Consider fundraising soon"} />
        )}
      </ResultsRegion>
    </div>
  );
}

function BurnRateCalc({ show }: { show: boolean }) {
  const [startCash, setStartCash] = useState("");
  const [endCash, setEndCash] = useState("");
  const [months, setMonths] = useState("");
  const burn = (parseFloat(startCash) - parseFloat(endCash)) / parseFloat(months);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <NumInput label="Starting Cash" value={startCash} onChange={setStartCash} prefix="$" />
        <NumInput label="Ending Cash" value={endCash} onChange={setEndCash} prefix="$" />
        <NumInput label="Number of Months" value={months} onChange={setMonths} />
      </div>
      <ResultsRegion>
        {show && startCash && endCash && months && parseFloat(months) > 0 && (
          <Result label="Monthly Burn Rate" value={fmt(burn)} />
        )}
      </ResultsRegion>
    </div>
  );
}

function LtvCalc({ show }: { show: boolean }) {
  const [arpu, setArpu] = useState("");
  const [lifespan, setLifespan] = useState("");
  const [margin, setMargin] = useState("");
  const ltv = parseFloat(arpu) * parseFloat(lifespan) * (parseFloat(margin || "100") / 100);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <NumInput label="Avg Revenue Per User / Month" value={arpu} onChange={setArpu} prefix="$" />
        <NumInput label="Avg Customer Lifespan (months)" value={lifespan} onChange={setLifespan} />
        <NumInput label="Gross Margin %" value={margin} onChange={setMargin} suffix="%" placeholder="100" />
      </div>
      <ResultsRegion>
        {show && arpu && lifespan && (
          <Result label="Customer Lifetime Value (LTV)" value={fmt(ltv)} />
        )}
      </ResultsRegion>
    </div>
  );
}

function CacCalc({ show }: { show: boolean }) {
  const [spend, setSpend] = useState("");
  const [customers, setCustomers] = useState("");
  const cac = parseFloat(spend) / parseFloat(customers);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NumInput label="Total Sales & Marketing Spend" value={spend} onChange={setSpend} prefix="$" />
        <NumInput label="New Customers Acquired" value={customers} onChange={setCustomers} />
      </div>
      <ResultsRegion>
        {show && spend && customers && parseFloat(customers) > 0 && (
          <Result label="Customer Acquisition Cost (CAC)" value={fmt(cac)} />
        )}
      </ResultsRegion>
    </div>
  );
}

function LtvCacCalc({ show }: { show: boolean }) {
  const [ltv, setLtv] = useState("");
  const [cac, setCac] = useState("");
  const ratio = parseFloat(ltv) / parseFloat(cac);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NumInput label="Customer Lifetime Value (LTV)" value={ltv} onChange={setLtv} prefix="$" />
        <NumInput label="Customer Acquisition Cost (CAC)" value={cac} onChange={setCac} prefix="$" />
      </div>
      <ResultsRegion>
        {show && ltv && cac && parseFloat(cac) > 0 && (
          <Result label="LTV / CAC Ratio" value={`${fmtNum(ratio)}:1`} sub={ratio >= 3 ? "Healthy ratio (3:1+)" : ratio >= 1 ? "Below target — aim for 3:1" : "Losing money on each customer"} />
        )}
      </ResultsRegion>
    </div>
  );
}

function TamCalc({ show }: { show: boolean }) {
  const [totalCustomers, setTotalCustomers] = useState("");
  const [annualValue, setAnnualValue] = useState("");
  const [samPct, setSamPct] = useState("");
  const [somPct, setSomPct] = useState("");
  const tam = parseFloat(totalCustomers) * parseFloat(annualValue);
  const sam = tam * (parseFloat(samPct) / 100);
  const som = sam * (parseFloat(somPct) / 100);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NumInput label="Total Potential Customers" value={totalCustomers} onChange={setTotalCustomers} />
        <NumInput label="Annual Contract Value" value={annualValue} onChange={setAnnualValue} prefix="$" />
        <NumInput label="% You Can Serve (SAM)" value={samPct} onChange={setSamPct} suffix="%" />
        <NumInput label="% You Can Capture (SOM)" value={somPct} onChange={setSomPct} suffix="%" />
      </div>
      <ResultsRegion>
        {show && totalCustomers && annualValue && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Result label="TAM (Total Addressable Market)" value={fmt(tam)} />
            {samPct && <Result label="SAM (Serviceable Available)" value={fmt(sam)} />}
            {samPct && somPct && <Result label="SOM (Serviceable Obtainable)" value={fmt(som)} />}
          </div>
        )}
      </ResultsRegion>
    </div>
  );
}

function ValuationCalc({ show }: { show: boolean }) {
  const [method, setMethod] = useState<"berkus" | "vc" | "scorecard">("berkus");
  const [soundIdea, setSoundIdea] = useState("");
  const [prototype, setPrototype] = useState("");
  const [team, setTeam] = useState("");
  const [strategicRel, setStrategicRel] = useState("");
  const [productRollout, setProductRollout] = useState("");
  const [estimatedRevenue, setEstimatedRevenue] = useState("");
  const [multiple, setMultiple] = useState("");
  const [investorOwnership, setInvestorOwnership] = useState("");
  const [avgPreMoney, setAvgPreMoney] = useState("");
  const [teamScore, setTeamScore] = useState("");
  const [marketScore, setMarketScore] = useState("");
  const [productScore, setProductScore] = useState("");
  const [competitionScore, setCompetitionScore] = useState("");
  const berkusTotal = [soundIdea, prototype, team, strategicRel, productRollout].reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  const vcPostMoney = (parseFloat(estimatedRevenue) * parseFloat(multiple)) / (parseFloat(investorOwnership) / 100);
  const vcPreMoney = vcPostMoney - (vcPostMoney * (parseFloat(investorOwnership) / 100));
  const scorecardMultiplier = ([teamScore, marketScore, productScore, competitionScore].reduce((sum, v) => sum + (parseFloat(v) || 100), 0) / 4) / 100;
  const scorecardVal = parseFloat(avgPreMoney) * scorecardMultiplier;
  return (
    <div className="space-y-4">
      <div className="flex gap-2 flex-wrap" role="group" aria-label="Valuation method selection">
        {(["berkus", "vc", "scorecard"] as const).map((m) => (
          <Button key={m} variant={method === m ? "default" : "outline"} size="sm" onClick={() => setMethod(m)} aria-pressed={method === m} aria-label={m === "berkus" ? "Berkus Method" : m === "vc" ? "VC Method" : "Scorecard Method"}>
            {m === "berkus" ? "Berkus Method" : m === "vc" ? "VC Method" : "Scorecard"}
          </Button>
        ))}
      </div>
      {method === "berkus" && (
        <>
          <p className="text-xs text-muted-foreground">Assign up to $500K for each factor (max $2.5M pre-money).</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <NumInput label="Sound Idea" value={soundIdea} onChange={setSoundIdea} prefix="$" />
            <NumInput label="Prototype / Technology" value={prototype} onChange={setPrototype} prefix="$" />
            <NumInput label="Quality Management Team" value={team} onChange={setTeam} prefix="$" />
            <NumInput label="Strategic Relationships" value={strategicRel} onChange={setStrategicRel} prefix="$" />
            <NumInput label="Product Rollout / Sales" value={productRollout} onChange={setProductRollout} prefix="$" />
          </div>
          <ResultsRegion>
            {show && <Result label="Berkus Valuation" value={fmt(berkusTotal)} />}
          </ResultsRegion>
        </>
      )}
      {method === "vc" && (
        <>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <NumInput label="Estimated Revenue (Year 5)" value={estimatedRevenue} onChange={setEstimatedRevenue} prefix="$" />
            <NumInput label="Industry P/E Multiple" value={multiple} onChange={setMultiple} placeholder="10" />
            <NumInput label="Investor Ownership %" value={investorOwnership} onChange={setInvestorOwnership} suffix="%" />
          </div>
          <ResultsRegion>
            {show && estimatedRevenue && multiple && investorOwnership && parseFloat(investorOwnership) > 0 && (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <Result label="Post-Money Valuation" value={fmt(vcPostMoney)} />
                <Result label="Pre-Money Valuation" value={fmt(vcPreMoney)} />
              </div>
            )}
          </ResultsRegion>
        </>
      )}
      {method === "scorecard" && (
        <>
          <p className="text-xs text-muted-foreground">Score each factor as a percentage of average (100% = average, 150% = above average).</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <NumInput label="Avg Pre-Money for Stage/Region" value={avgPreMoney} onChange={setAvgPreMoney} prefix="$" />
            <NumInput label="Team Score %" value={teamScore} onChange={setTeamScore} suffix="%" placeholder="100" />
            <NumInput label="Market Opportunity Score %" value={marketScore} onChange={setMarketScore} suffix="%" placeholder="100" />
            <NumInput label="Product Score %" value={productScore} onChange={setProductScore} suffix="%" placeholder="100" />
            <NumInput label="Competition Score %" value={competitionScore} onChange={setCompetitionScore} suffix="%" placeholder="100" />
          </div>
          <ResultsRegion>
            {show && avgPreMoney && (
              <Result label="Scorecard Valuation" value={fmt(scorecardVal)} sub={`Multiplier: ${(scorecardMultiplier * 100).toFixed(0)}%`} />
            )}
          </ResultsRegion>
        </>
      )}
    </div>
  );
}

function CapTableCalc({ show }: { show: boolean }) {
  const [founderShares, setFounderShares] = useState("");
  const [investmentAmount, setInvestmentAmount] = useState("");
  const [preMoneyVal, setPreMoneyVal] = useState("");
  const [optionPool, setOptionPool] = useState("");
  const postMoney = parseFloat(preMoneyVal) + parseFloat(investmentAmount);
  const investorPct = postMoney > 0 ? (parseFloat(investmentAmount) / postMoney) * 100 : 0;
  const poolPct = Math.min(parseFloat(optionPool) || 0, 100 - investorPct);
  const founderPct = Math.max(0, 100 - investorPct - poolPct);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NumInput label="Pre-Money Valuation" value={preMoneyVal} onChange={setPreMoneyVal} prefix="$" />
        <NumInput label="Investment Amount" value={investmentAmount} onChange={setInvestmentAmount} prefix="$" />
        <NumInput label="Employee Option Pool %" value={optionPool} onChange={setOptionPool} suffix="%" placeholder="10" />
        <NumInput label="Founder Shares (for reference)" value={founderShares} onChange={setFounderShares} />
      </div>
      <ResultsRegion>
        {show && preMoneyVal && investmentAmount && parseFloat(preMoneyVal) > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Result label="Founder Ownership" value={fmtPct(founderPct)} />
            <Result label="Investor Ownership" value={fmtPct(investorPct)} />
            <Result label="Option Pool" value={fmtPct(poolPct)} />
          </div>
        )}
      </ResultsRegion>
    </div>
  );
}

function SaasPricingCalc({ show }: { show: boolean }) {
  const [arpu, setArpu] = useState("");
  const [customers, setCustomers] = useState("");
  const [growthRate, setGrowthRate] = useState("");
  const [conversionRate, setConversionRate] = useState("");
  const [annualDiscount, setAnnualDiscount] = useState("");
  const conv = parseFloat(conversionRate) || 100;
  const effectiveCustomers = parseFloat(customers) * (conv / 100);
  const effectiveMonthlyRev = parseFloat(arpu) * effectiveCustomers;
  const disc = parseFloat(annualDiscount) || 0;
  const annualPricePerUser = parseFloat(arpu) * 12 * (1 - disc / 100);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NumInput label="Monthly ARPU" value={arpu} onChange={setArpu} prefix="$" />
        <NumInput label="Target Customers" value={customers} onChange={setCustomers} />
        <NumInput label="Monthly Growth Rate %" value={growthRate} onChange={setGrowthRate} suffix="%" />
        <NumInput label="Conversion Rate %" value={conversionRate} onChange={setConversionRate} suffix="%" placeholder="100" />
        <NumInput label="Annual Billing Discount %" value={annualDiscount} onChange={setAnnualDiscount} suffix="%" placeholder="0" />
      </div>
      <ResultsRegion>
        {show && arpu && customers && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Result label="Monthly Revenue" value={fmt(effectiveMonthlyRev)} />
            <Result label="Annual Revenue" value={fmt(effectiveMonthlyRev * 12)} />
            {annualDiscount && <Result label="Annual Price / User" value={fmt(annualPricePerUser)} sub={`${disc}% discount vs monthly`} />}
          </div>
        )}
      </ResultsRegion>
    </div>
  );
}

function BreakEvenCalc({ show }: { show: boolean }) {
  const [fixedCosts, setFixedCosts] = useState("");
  const [variableCost, setVariableCost] = useState("");
  const [revenuePerCustomer, setRevenuePerCustomer] = useState("");
  const [customersPerMonth, setCustomersPerMonth] = useState("");
  const contribution = parseFloat(revenuePerCustomer) - parseFloat(variableCost);
  const breakEvenCustomers = parseFloat(fixedCosts) / contribution;
  const monthsToBreakEven = breakEvenCustomers / parseFloat(customersPerMonth);
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NumInput label="Monthly Fixed Costs" value={fixedCosts} onChange={setFixedCosts} prefix="$" />
        <NumInput label="Variable Cost / Customer" value={variableCost} onChange={setVariableCost} prefix="$" />
        <NumInput label="Revenue / Customer / Month" value={revenuePerCustomer} onChange={setRevenuePerCustomer} prefix="$" />
        <NumInput label="New Customers / Month" value={customersPerMonth} onChange={setCustomersPerMonth} />
      </div>
      <ResultsRegion>
        {show && fixedCosts && variableCost && revenuePerCustomer && contribution > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Result label="Break-Even Customers" value={fmtNum(breakEvenCustomers)} />
            {customersPerMonth && parseFloat(customersPerMonth) > 0 && (
              <Result label="Time to Break Even" value={`${fmtNum(monthsToBreakEven)} months`} />
            )}
          </div>
        )}
      </ResultsRegion>
    </div>
  );
}

function ChurnCalc({ show }: { show: boolean }) {
  const [mrr, setMrr] = useState("");
  const [churnRate, setChurnRate] = useState("");
  const [months, setMonths] = useState("12");
  const m = parseInt(months) || 12;
  const startMrr = parseFloat(mrr);
  const churn = parseFloat(churnRate) / 100;
  const endMrr = startMrr * Math.pow(1 - churn, m);
  const lostRev = startMrr * m - Array.from({ length: m }, (_, i) => startMrr * Math.pow(1 - churn, i)).reduce((a, b) => a + b, 0);
  const effectiveLtv = churn > 0 ? startMrr / churn : 0;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        <NumInput label="Current MRR" value={mrr} onChange={setMrr} prefix="$" />
        <NumInput label="Monthly Churn Rate %" value={churnRate} onChange={setChurnRate} suffix="%" />
        <NumInput label="Forecast Period (months)" value={months} onChange={setMonths} placeholder="12" />
      </div>
      <ResultsRegion>
        {show && mrr && churnRate && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Result label={`MRR After ${m} Months`} value={fmt(endMrr)} sub={`${fmtPct(((startMrr - endMrr) / startMrr) * 100)} revenue loss`} />
            <Result label="Revenue Lost to Churn" value={fmt(lostRev)} />
            <Result label="Implied LTV per Customer" value={fmt(effectiveLtv)} />
          </div>
        )}
      </ResultsRegion>
    </div>
  );
}

function RevForecastCalc({ show }: { show: boolean }) {
  const [mrr, setMrr] = useState("");
  const [growthRate, setGrowthRate] = useState("");
  const [churnRate, setChurnRate] = useState("");
  const [expansionRate, setExpansionRate] = useState("");
  const startMrr = parseFloat(mrr);
  const growth = parseFloat(growthRate) / 100;
  const churn = parseFloat(churnRate) / 100;
  const expansion = parseFloat(expansionRate || "0") / 100;
  const netGrowth = growth - churn + expansion;
  const projections = Array.from({ length: 36 }, (_, i) => startMrr * Math.pow(1 + netGrowth, i + 1));
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NumInput label="Current MRR" value={mrr} onChange={setMrr} prefix="$" />
        <NumInput label="Monthly Growth Rate %" value={growthRate} onChange={setGrowthRate} suffix="%" />
        <NumInput label="Monthly Churn Rate %" value={churnRate} onChange={setChurnRate} suffix="%" />
        <NumInput label="Monthly Expansion Rate %" value={expansionRate} onChange={setExpansionRate} suffix="%" placeholder="0" />
      </div>
      <ResultsRegion>
        {show && mrr && growthRate && churnRate && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Result label="MRR at Month 12" value={fmt(projections[11])} sub={`ARR: ${fmt(projections[11] * 12)}`} />
            <Result label="MRR at Month 24" value={fmt(projections[23])} sub={`ARR: ${fmt(projections[23] * 12)}`} />
            <Result label="MRR at Month 36" value={fmt(projections[35])} sub={`ARR: ${fmt(projections[35] * 12)}`} />
          </div>
        )}
      </ResultsRegion>
    </div>
  );
}

function HiringCostCalc({ show }: { show: boolean }) {
  const [salary, setSalary] = useState("");
  const [benefits, setBenefits] = useState("");
  const [taxes, setTaxes] = useState("");
  const [equipment, setEquipment] = useState("");
  const [recruiting, setRecruiting] = useState("");
  const total = [salary, benefits, taxes, equipment, recruiting].reduce((sum, v) => sum + (parseFloat(v) || 0), 0);
  const monthlyCost = total / 12;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NumInput label="Annual Salary" value={salary} onChange={setSalary} prefix="$" />
        <NumInput label="Annual Benefits" value={benefits} onChange={setBenefits} prefix="$" />
        <NumInput label="Payroll Taxes (Annual)" value={taxes} onChange={setTaxes} prefix="$" />
        <NumInput label="Equipment & Setup" value={equipment} onChange={setEquipment} prefix="$" />
        <NumInput label="Recruiting Cost" value={recruiting} onChange={setRecruiting} prefix="$" />
      </div>
      <ResultsRegion>
        {show && salary && (
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            <Result label="Total Annual Cost" value={fmt(total)} />
            <Result label="Monthly Cost" value={fmt(monthlyCost)} sub={`${fmtPct(((total - parseFloat(salary)) / parseFloat(salary)) * 100)} above base salary`} />
          </div>
        )}
      </ResultsRegion>
    </div>
  );
}

function AiRoiCalc({ show }: { show: boolean }) {
  const [employeesReplaced, setEmployeesReplaced] = useState("");
  const [avgSalary, setAvgSalary] = useState("");
  const [automationCost, setAutomationCost] = useState("");
  const [implTime, setImplTime] = useState("");
  const annualSavings = parseFloat(employeesReplaced) * parseFloat(avgSalary);
  const autoCost = parseFloat(automationCost) || 0;
  const netSavings = annualSavings - autoCost;
  const roi = autoCost > 0 ? (netSavings / autoCost) * 100 : 0;
  const paybackMonths = annualSavings > 0 ? (autoCost / (annualSavings / 12)) : 0;
  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <NumInput label="FTEs Replaced / Reallocated" value={employeesReplaced} onChange={setEmployeesReplaced} />
        <NumInput label="Avg Annual Salary per FTE" value={avgSalary} onChange={setAvgSalary} prefix="$" />
        <NumInput label="Total Automation Cost" value={automationCost} onChange={setAutomationCost} prefix="$" />
        <NumInput label="Implementation Time (months)" value={implTime} onChange={setImplTime} />
      </div>
      <ResultsRegion>
        {show && employeesReplaced && avgSalary && automationCost && (
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <Result label="Annual Cost Savings" value={fmt(annualSavings)} />
            <Result label="First-Year ROI" value={fmtPct(roi)} sub={roi > 0 ? "Positive return" : "Negative return"} />
            <Result label="Payback Period" value={`${fmtNum(paybackMonths)} months`} />
          </div>
        )}
      </ResultsRegion>
    </div>
  );
}

function EquitySplitCalc({ show }: { show: boolean }) {
  const [founders, setFounders] = useState([
    { name: "Founder 1", capital: "", time: "", ip: "", contribution: "" },
    { name: "Founder 2", capital: "", time: "", ip: "", contribution: "" },
  ]);
  const updateFounder = (i: number, field: string, value: string) => {
    const updated = [...founders];
    (updated[i] as any)[field] = value;
    setFounders(updated);
  };
  const addFounder = () => setFounders([...founders, { name: `Founder ${founders.length + 1}`, capital: "", time: "", ip: "", contribution: "" }]);
  const scores = founders.map((f) => (parseFloat(f.capital) || 0) + (parseFloat(f.time) || 0) + (parseFloat(f.ip) || 0) + (parseFloat(f.contribution) || 0));
  const totalScore = scores.reduce((a, b) => a + b, 0);
  return (
    <div className="space-y-4">
      {founders.map((f, i) => (
        <div key={i} className="border border-white/5 rounded-lg p-4 space-y-3">
          <Input value={f.name} onChange={(e) => updateFounder(i, "name", e.target.value)} className="font-semibold" data-testid={`input-founder-name-${i}`} aria-label={`Founder ${i + 1} name`} />
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <NumInput label="Capital Invested" value={f.capital} onChange={(v) => updateFounder(i, "capital", v)} />
            <NumInput label="Time Commitment" value={f.time} onChange={(v) => updateFounder(i, "time", v)} />
            <NumInput label="IP / Expertise" value={f.ip} onChange={(v) => updateFounder(i, "ip", v)} />
            <NumInput label="Other Contribution" value={f.contribution} onChange={(v) => updateFounder(i, "contribution", v)} />
          </div>
          <ResultsRegion>
            {show && totalScore > 0 && (
              <p className="text-sm font-semibold text-primary" data-testid={`result-equity-${i}`}>Suggested Equity: {fmtPct((scores[i] / totalScore) * 100)}</p>
            )}
          </ResultsRegion>
        </div>
      ))}
      <Button variant="outline" size="sm" onClick={addFounder} data-testid="button-add-founder">+ Add Founder</Button>
    </div>
  );
}

// ── Calculator Registry ──

interface CalcMeta {
  title: string;
  keyword: string;
  description: string;
  whyItMatters: string;
  component: (props: { show: boolean }) => React.JSX.Element;
  faqs: { q: string; a: string }[];
  examples: { title: string; description: string }[];
}

const CALCULATOR_DATA: Record<string, CalcMeta> = {
  "startup-runway": {
    title: "Startup Runway Calculator",
    keyword: "startup runway calculator",
    description: "Your startup runway is the number of months you can continue operating before running out of cash. It's the single most important number every founder should know. This calculator divides your available cash by your monthly burn rate to give you a clear picture of how much time you have left.",
    whyItMatters: "Runway determines when you need to fundraise, when to cut costs, and whether you can afford to take risks. Running out of runway is the number one reason startups fail. Investors will ask about your runway in every meeting — know your number cold.",
    component: RunwayCalc,
    faqs: [
      { q: "How much runway should a startup have?", a: "Most investors and advisors recommend 12–18 months of runway at pre-seed, and 18–24 months at seed stage. Less than 6 months is considered a red flag that requires immediate action — either fundraising, cutting costs, or accelerating revenue." },
      { q: "What's the difference between runway and burn rate?", a: "Burn rate is how much cash you spend each month. Runway is how long your cash will last at that burn rate. Runway = Cash on Hand ÷ Monthly Burn Rate. They're related but distinct — burn rate is the speed, runway is the distance." },
      { q: "When should I start fundraising based on my runway?", a: "Start raising your next round when you have 6–9 months of runway remaining. Fundraising typically takes 3–6 months, so starting at the 6-month mark gives you enough buffer to run a proper process without appearing desperate." },
      { q: "Should I use net burn or gross burn for runway?", a: "Use net burn (total spend minus revenue) for your primary runway calculation, since it reflects actual cash depletion. However, also calculate runway using gross burn as a worst-case scenario — this shows what happens if revenue drops to zero." },
    ],
    examples: [
      { title: "Pre-Seed Startup", description: "A 3-person team with $400K in the bank and a monthly burn rate of $25K has 16 months of runway — right in the sweet spot for pre-seed. They should start their seed fundraise around month 7–10." },
      { title: "Post-Seed SaaS Company", description: "A 12-person team with $1.8M remaining and $120K/month burn has 15 months of runway. With $40K in monthly revenue, their net burn is $80K, giving them 22.5 months — a much healthier picture." },
    ],
  },
  "burn-rate": {
    title: "Burn Rate Calculator",
    keyword: "startup burn rate calculator",
    description: "Your burn rate measures how quickly your startup is spending cash each month. This calculator takes your starting and ending cash balances over a period and calculates the average monthly cash consumption.",
    whyItMatters: "Burn rate is the foundation of financial planning for startups. It determines your runway, informs fundraising timelines, and signals to investors whether you're being responsible with capital. A burn rate that's too high relative to progress is one of the most common reasons investors pass on deals.",
    component: BurnRateCalc,
    faqs: [
      { q: "What is a good burn rate for a startup?", a: "There's no universal 'good' burn rate — it depends on your stage, revenue, and funding. Pre-seed startups typically burn $15K–$40K/month. Seed-stage companies burn $50K–$150K/month. The key metric is burn efficiency: how much progress are you making per dollar spent?" },
      { q: "How do I reduce my burn rate quickly?", a: "The fastest levers are: renegotiating vendor contracts (many SaaS companies offer startup discounts), reducing founder salaries temporarily, consolidating software tools, and pausing non-essential hiring. Cloud cost optimization can also yield significant savings." },
      { q: "How often should I track burn rate?", a: "Track burn rate monthly at minimum. Review it with your co-founders weekly during cash-critical periods. Share it with your board quarterly. Sudden increases in burn rate should be investigated immediately — they often signal scope creep or unplanned expenses." },
    ],
    examples: [
      { title: "Calculating Monthly Burn", description: "A startup began the quarter with $600K and ended with $450K after 3 months. Monthly burn rate = ($600K - $450K) / 3 = $50K/month. At this rate, their remaining $450K will last 9 months." },
      { title: "Tracking Burn After Hiring", description: "After hiring 3 engineers, a startup's cash went from $1.2M to $900K over 4 months. Burn rate = ($1.2M - $900K) / 4 = $75K/month. Before hiring, burn was $40K/month — the team added $35K/month in costs." },
    ],
  },
  "ltv": {
    title: "Customer Lifetime Value (LTV) Calculator",
    keyword: "customer lifetime value calculator",
    description: "Customer Lifetime Value (LTV) estimates the total revenue a single customer will generate over the entire duration of their relationship with your company. This calculator uses average revenue per user, customer lifespan, and gross margin to compute LTV.",
    whyItMatters: "LTV is one of the most important SaaS metrics. It tells you how much you can afford to spend acquiring customers (CAC), whether your business model is sustainable, and how valuable each customer relationship truly is. Investors use LTV to evaluate the long-term economics of your business.",
    component: LtvCalc,
    faqs: [
      { q: "What is a good LTV for a SaaS company?", a: "LTV varies enormously by segment. SMB SaaS products might have LTVs of $500–$5,000. Mid-market SaaS typically ranges from $5,000–$50,000. Enterprise SaaS can exceed $100,000+. The absolute number matters less than its relationship to CAC — you want an LTV/CAC ratio of at least 3:1." },
      { q: "How do I increase customer LTV?", a: "Three main levers: increase ARPU (through upsells, price increases, or premium tiers), increase customer lifespan (by reducing churn through better onboarding, support, and product improvements), or improve gross margins (by reducing cost of delivery)." },
      { q: "Should I use gross margin in the LTV calculation?", a: "Yes. LTV calculated with gross margin (revenue × margin × lifespan) gives a more accurate picture because it accounts for the cost of serving customers. Raw revenue LTV overstates the actual value of customers and can lead to overspending on acquisition." },
    ],
    examples: [
      { title: "SaaS Product with Low Churn", description: "A B2B SaaS tool charges $99/month with an average customer lifespan of 36 months and 80% gross margin. LTV = $99 × 36 × 0.80 = $2,851. If CAC is $500, the LTV/CAC ratio is 5.7:1 — excellent unit economics." },
      { title: "Consumer Subscription", description: "A consumer app charges $12/month with an average lifespan of 8 months and 90% margin. LTV = $12 × 8 × 0.90 = $86.40. This means they can't spend more than ~$29 to acquire a customer (targeting 3:1 ratio)." },
    ],
  },
  "cac": {
    title: "Customer Acquisition Cost (CAC) Calculator",
    keyword: "customer acquisition cost calculator",
    description: "Customer Acquisition Cost (CAC) measures how much you spend to acquire a single new customer. This calculator divides your total sales and marketing spend by the number of new customers acquired during the same period.",
    whyItMatters: "CAC is one of the most scrutinized metrics in startup fundraising. It directly impacts your unit economics, profitability timeline, and how efficiently you're deploying capital. A CAC that's too high relative to LTV signals an unsustainable business model.",
    component: CacCalc,
    faqs: [
      { q: "What costs should I include in CAC?", a: "Include all sales and marketing costs: ad spend, sales team salaries and commissions, marketing tools and software, content creation costs, event sponsorships, and any other expenses directly tied to customer acquisition. Some companies also include a portion of overhead." },
      { q: "How do I reduce my CAC?", a: "Focus on organic channels (SEO, content marketing, referrals), optimize paid ad targeting and conversion funnels, improve your product's viral coefficient, leverage partnerships and integrations, and ensure your sales team is efficient with qualified leads rather than cold outreach." },
      { q: "What's the difference between blended and paid CAC?", a: "Blended CAC includes all customers (organic + paid). Paid CAC only counts customers from paid channels against paid channel costs. Blended CAC is usually lower and gives a holistic view. Paid CAC helps you evaluate the efficiency of your marketing spend specifically." },
    ],
    examples: [
      { title: "B2B SaaS Company", description: "A startup spent $45,000 on marketing and $30,000 on sales in Q1, acquiring 50 new customers. CAC = $75,000 / 50 = $1,500 per customer. With an LTV of $6,000, their LTV/CAC ratio is 4:1." },
      { title: "E-Commerce Startup", description: "An e-commerce brand spent $20,000 on Facebook and Google ads in January, generating 400 new customers. CAC = $20,000 / 400 = $50. With an average order value of $80 and 2.5 orders per customer lifetime, LTV is $200, giving a 4:1 ratio." },
    ],
  },
  "ltv-cac-ratio": {
    title: "LTV / CAC Ratio Calculator",
    keyword: "ltv cac ratio calculator",
    description: "The LTV/CAC ratio compares the lifetime value of a customer to the cost of acquiring them. It's the single best indicator of whether your business model is economically viable. This calculator divides your LTV by your CAC to produce the ratio investors care about most.",
    whyItMatters: "Investors consider the LTV/CAC ratio the gold standard for evaluating unit economics. A ratio below 1:1 means you're losing money on every customer. Between 1:1 and 3:1 means the business works but isn't efficient yet. Above 3:1 is the target for healthy SaaS businesses. Above 5:1 may indicate you're under-investing in growth.",
    component: LtvCacCalc,
    faqs: [
      { q: "What LTV/CAC ratio do investors look for?", a: "Most venture investors want to see at least 3:1. This means for every $1 spent acquiring a customer, the company earns $3 in lifetime value. A ratio of 3:1 to 5:1 is considered healthy. Below 3:1 suggests the business needs better unit economics. Above 5:1 may mean you could be growing faster." },
      { q: "How long should CAC payback take?", a: "Ideally, you should recover your CAC within 12–18 months. This is called the 'CAC payback period.' If it takes longer than 18 months, you're tying up too much capital in acquisition. SMB SaaS should aim for under 12 months; enterprise can tolerate 12–18 months due to higher contract values." },
      { q: "Can LTV/CAC be too high?", a: "Yes. If your ratio exceeds 5:1, it might mean you're under-investing in sales and marketing. You could likely spend more aggressively on acquisition and still maintain healthy economics while growing faster. This is a 'good problem' but still something investors will flag." },
    ],
    examples: [
      { title: "Healthy SaaS Business", description: "A SaaS company with an LTV of $9,000 and CAC of $2,500 has an LTV/CAC ratio of 3.6:1 — right in the healthy zone. They can grow confidently, knowing each customer generates strong returns." },
      { title: "Early-Stage Warning Sign", description: "A startup with an LTV of $800 and CAC of $600 has a ratio of only 1.3:1. This means almost all the revenue from each customer goes to covering their acquisition cost, leaving little for operating expenses or profit." },
    ],
  },
  "tam-sam-som": {
    title: "TAM / SAM / SOM Market Size Calculator",
    keyword: "tam sam som calculator",
    description: "TAM (Total Addressable Market), SAM (Serviceable Available Market), and SOM (Serviceable Obtainable Market) are the three layers of market sizing used in every investor pitch deck. This calculator helps you estimate all three based on your total potential customers, annual contract value, and market reach percentages.",
    whyItMatters: "Market size is one of the first things investors evaluate. A TAM that's too small signals limited upside. A SOM that's unrealistically large signals naivety. Getting these numbers right — and being able to defend them — is critical for fundraising and strategic planning.",
    component: TamCalc,
    faqs: [
      { q: "What's the difference between TAM, SAM, and SOM?", a: "TAM is the total revenue opportunity if you captured 100% of the market. SAM is the portion of TAM you can realistically serve given your product, geography, and business model. SOM is the portion of SAM you can realistically capture in the near term — your realistic short-term market share." },
      { q: "How big should my TAM be for venture investors?", a: "Most venture investors look for a TAM of at least $1B. This doesn't mean you'll capture $1B — it means the market is large enough to support a venture-scale outcome. If your TAM is below $500M, VCs may consider it too small for their model." },
      { q: "Should I use top-down or bottom-up market sizing?", a: "Bottom-up is more credible. Start with the number of potential customers, multiply by what each would pay, and build up to TAM. Top-down (starting from industry reports and taking a percentage) is easier but less convincing to sophisticated investors." },
    ],
    examples: [
      { title: "B2B SaaS Market Sizing", description: "There are 500,000 small accounting firms globally, each worth $2,400/year in software spend. TAM = $1.2B. You serve English-speaking markets (40% = SAM of $480M). You can realistically capture 2% in 3 years (SOM of $9.6M)." },
      { title: "Vertical SaaS Niche", description: "There are 12,000 dental practices in your region, each willing to pay $6,000/year. TAM = $72M. Your product fits 60% of them (SAM = $43.2M). You target 5% market share initially (SOM = $2.16M)." },
    ],
  },
  "startup-valuation": {
    title: "Startup Valuation Calculator",
    keyword: "startup valuation calculator",
    description: "Estimating your startup's pre-money valuation is one of the most challenging aspects of fundraising. This calculator implements three widely-used methods — the Berkus Method, the Venture Capital Method, and the Scorecard Method — to help you triangulate a reasonable valuation before your seed round.",
    whyItMatters: "Getting your valuation right impacts everything: how much equity you give up, your relationship with investors, and your ability to raise future rounds. Price too high and you won't find investors. Price too low and you dilute yourself unnecessarily. These frameworks give you a defensible starting point for negotiations.",
    component: ValuationCalc,
    faqs: [
      { q: "Which valuation method should I use?", a: "Use all three and compare the results. The Berkus Method works best for pre-revenue startups. The VC Method is ideal when you can project future revenue. The Scorecard Method is useful when you have comparable companies to benchmark against. The overlap between methods gives you a defensible range." },
      { q: "What's a typical pre-seed/seed valuation?", a: "Pre-seed valuations typically range from $1M–$5M. Seed valuations typically range from $3M–$15M. These vary significantly by geography, industry, founder experience, and market conditions. Silicon Valley valuations tend to be 2–3x higher than other markets." },
      { q: "How do I defend my valuation to investors?", a: "Use data, not feelings. Show comparable transactions (similar companies that raised at similar valuations), demonstrate traction metrics, explain your market opportunity, and walk through the methodology. Investors respect founders who can articulate how they arrived at their number." },
    ],
    examples: [
      { title: "Berkus Method for Pre-Revenue Startup", description: "A founder with a strong prototype ($400K), experienced team ($500K), initial partnerships ($200K), good market validation ($300K), and early sales conversations ($100K) arrives at a $1.5M Berkus valuation." },
      { title: "VC Method for Seed Stage", description: "Estimated Year 5 revenue of $20M with a 6x industry multiple gives a $120M terminal value. If the investor wants 25% ownership, post-money is $120M / 0.25 = $480M, making the pre-money valuation approximately $360M (adjusted for risk and discount rates)." },
    ],
  },
  "cap-table": {
    title: "Cap Table Dilution Calculator",
    keyword: "cap table dilution calculator",
    description: "A cap table (capitalization table) shows who owns what percentage of your company. This calculator models how a funding round affects founder ownership, investor ownership, and the employee option pool — the three most critical elements of any fundraising negotiation.",
    whyItMatters: "Dilution is one of the most misunderstood aspects of fundraising. Founders need to understand exactly how much ownership they're giving up in each round, how the option pool affects their stake, and what their ownership will look like after multiple rounds. Getting this wrong can mean losing control of your own company.",
    component: CapTableCalc,
    faqs: [
      { q: "How much dilution is normal per round?", a: "Typically 15–25% per round. Pre-seed rounds might dilute 10–20%. Seed rounds usually dilute 15–25%. Series A can dilute 20–30%. After 3 rounds, a solo founder might own 30–45% of the company. This is normal and expected." },
      { q: "What's a standard option pool size?", a: "Most investors expect a 10–20% option pool (on a fully diluted basis) before investment. This pool is typically created from the founders' shares (pre-money), which effectively increases dilution beyond the headline percentage. Negotiate the pool size carefully." },
      { q: "Should the option pool come from pre-money or post-money?", a: "Investors almost always want it pre-money (before their investment), which means founders bear the entire dilution of the option pool. Some founders negotiate to split it or include it post-money. This is a significant negotiation point that can affect your ownership by 5–10%." },
    ],
    examples: [
      { title: "Seed Round Dilution", description: "Pre-money valuation of $4M with a $1M investment. Post-money = $5M. Investor gets 20% ($1M/$5M). With a 15% option pool, founders retain 65%. Before the round, founders had 100% — this single round cost them 35%." },
      { title: "Series A Impact", description: "Pre-money of $15M with $5M invested. Post-money = $20M. New investor gets 25%. Adding a 10% option pool expansion, founders' existing 65% stake gets diluted to approximately 42.25%." },
    ],
  },
  "saas-pricing": {
    title: "SaaS Pricing Calculator",
    keyword: "saas pricing calculator",
    description: "Pricing is one of the highest-leverage decisions a SaaS founder can make. This calculator helps you model different pricing scenarios by adjusting ARPU, customer count, growth rate, conversion rate, and the impact of annual vs monthly billing.",
    whyItMatters: "A 10% price increase often flows directly to the bottom line, making pricing one of the fastest ways to improve unit economics. Yet most founders underprice their product. This calculator helps you see the revenue impact of different pricing strategies before committing.",
    component: SaasPricingCalc,
    faqs: [
      { q: "Should I offer monthly or annual pricing?", a: "Offer both. Annual plans typically come with a 10–20% discount and provide upfront cash (improving cash flow) while reducing churn (annual customers churn less than monthly). Many SaaS companies see 30–50% of customers choose annual plans when the discount is compelling." },
      { q: "How do I know if my pricing is too low?", a: "Signs you're underpriced: conversion rates above 40% (price isn't a barrier), very low churn (customers see massive value), customers never negotiate, and competitors charge 2–5x more for similar features. If any of these are true, test a price increase." },
      { q: "How often should I change pricing?", a: "Review pricing at least annually. Many successful SaaS companies adjust pricing every 6–12 months. Grandfather existing customers on old pricing (or give them advance notice), and test new pricing with new customers first. Small incremental increases are less risky than large jumps." },
    ],
    examples: [
      { title: "Monthly vs Annual Revenue Impact", description: "At $49/month ARPU with 200 customers and 100% conversion, monthly revenue is $9,800. Offering a 20% annual discount ($470/year instead of $588) and converting 40% of customers to annual plans generates $37,600 upfront annually while reducing monthly billing revenue." },
      { title: "Price Increase Modeling", description: "Increasing ARPU from $29 to $39/month with 500 customers means going from $14,500 to $19,500/month — an extra $60,000/year in revenue. Even if you lose 10% of customers, you net $17,550/month, still ahead." },
    ],
  },
  "break-even": {
    title: "Break-Even Calculator",
    keyword: "startup break even calculator",
    description: "The break-even point is when your startup's revenue equals its costs — the moment you stop losing money. This calculator determines how many customers you need and how long it will take to reach profitability based on your fixed costs, variable costs, and revenue per customer.",
    whyItMatters: "Every startup needs to know when it will become self-sustaining. Break-even analysis helps you set realistic goals, plan hiring, and communicate credible timelines to investors. In today's fundraising environment, a clear path to profitability is more important than ever.",
    component: BreakEvenCalc,
    faqs: [
      { q: "When should a startup expect to break even?", a: "It varies by industry and model. SaaS companies often target break-even within 18–36 months of reaching product-market fit. Marketplace businesses may take longer due to the need to build both sides. Hardware companies typically have longer timelines. The key is having a realistic plan, not an arbitrary deadline." },
      { q: "What's the difference between break-even on a unit basis vs company basis?", a: "Unit break-even means each customer is profitable (revenue > variable cost per customer). Company break-even means total revenue covers all costs including fixed overhead. You should achieve unit break-even first — if individual customers aren't profitable, adding more won't help." },
      { q: "How do I speed up my time to break even?", a: "Three approaches: increase revenue per customer (raise prices, upsell), decrease variable costs (improve operational efficiency), or decrease fixed costs (reduce overhead). Often the highest-impact lever is pricing — a 15% price increase can significantly accelerate break-even." },
    ],
    examples: [
      { title: "SaaS Break-Even", description: "Fixed costs of $30,000/month, variable cost of $5 per customer, revenue of $50 per customer. Contribution margin = $45. Break-even customers = $30,000 / $45 = 667 customers. At 50 new customers/month, break-even in ~13 months." },
      { title: "E-Commerce Break-Even", description: "Fixed costs of $15,000/month, $25 variable cost per order, $65 revenue per order. Contribution margin = $40. Need 375 orders/month to break even. At 20 orders/day, that's about 19 days — so monthly profitability is achievable." },
    ],
  },
  "churn-impact": {
    title: "SaaS Churn Impact Calculator",
    keyword: "saas churn rate calculator",
    description: "Churn is the silent killer of SaaS businesses. Even small monthly churn rates compound dramatically over time, eroding your MRR, shortening customer LTV, and undermining growth. This calculator shows the true impact of churn on your revenue over any time period.",
    whyItMatters: "A 5% monthly churn rate means you lose nearly half your customers every year. Most founders underestimate churn's compounding effect. This calculator reveals the real cost of churn so you can prioritize retention — often the highest-ROI investment a SaaS company can make.",
    component: ChurnCalc,
    faqs: [
      { q: "What's a good churn rate for SaaS?", a: "Benchmarks vary by segment: Enterprise SaaS targets <1% monthly churn. Mid-market aims for 1–2%. SMB SaaS considers 3–5% acceptable but challenging. Consumer subscriptions typically see 5–10%. Lower churn is always better — even 1% improvement dramatically impacts long-term revenue." },
      { q: "What's the difference between logo churn and revenue churn?", a: "Logo churn counts the percentage of customers who leave. Revenue churn counts the percentage of revenue lost. They can differ significantly: losing 10 small customers costs less than losing 1 enterprise customer. Track both, but revenue churn is what impacts your business financially." },
      { q: "How do I reduce churn?", a: "Focus on onboarding (customers who activate fully churn less), build engagement features (habit-forming product loops), offer proactive customer success (reach out before customers disengage), and analyze churned customers for patterns (price sensitivity, missing features, competitive losses)." },
    ],
    examples: [
      { title: "Impact of 5% Monthly Churn", description: "Starting MRR of $100,000 with 5% monthly churn. After 12 months, MRR drops to $54,036 — you've lost nearly half your revenue. Revenue lost to churn over the year totals $272,636, and implied LTV per customer is only 20 months of revenue." },
      { title: "Reducing Churn from 4% to 2%", description: "At 4% monthly churn, $100K MRR becomes $61,270 after 12 months. At 2% monthly churn, it becomes $78,490 — a difference of $17,220/month by year's end. Over 12 months, that's $106,800 in additional revenue from a 2-point churn improvement." },
    ],
  },
  "revenue-forecast": {
    title: "Revenue Forecast Calculator",
    keyword: "startup revenue forecast calculator",
    description: "Revenue forecasting helps you model what your MRR and ARR will look like over the next 12–36 months. This calculator accounts for monthly growth rate, churn, and expansion revenue to produce realistic projections — not the hockey-stick fantasies that make investors roll their eyes.",
    whyItMatters: "Credible revenue forecasts are essential for fundraising, hiring plans, and strategic decision-making. Investors want to see that you can model your business thoughtfully. This calculator helps you build a bottom-up forecast that accounts for both growth and headwinds.",
    component: RevForecastCalc,
    faqs: [
      { q: "How accurate should my revenue forecast be?", a: "Early-stage forecasts are inherently uncertain. The goal isn't precision — it's demonstrating that you understand your business drivers. Show your assumptions clearly (growth rate, churn, expansion) and present multiple scenarios (base, optimistic, conservative). Being within 20% of your base case is excellent." },
      { q: "What's a good monthly growth rate?", a: "YC's famous benchmark is 5–7% weekly growth during the program. Monthly, 15–20% is considered strong for early-stage startups. As you scale, growth rates naturally decline: 10–15% at $1M ARR, 5–10% at $5M ARR, 3–5% at $20M+ ARR. Sustaining high growth is harder the bigger you get." },
      { q: "What is expansion revenue?", a: "Expansion revenue is additional revenue from existing customers through upsells, cross-sells, or usage-based growth. Net revenue retention above 100% means expansion revenue exceeds churn — your existing customer base grows even without new customers. Top SaaS companies achieve 120–140% net revenue retention." },
    ],
    examples: [
      { title: "Seed-Stage Projection", description: "Starting at $20K MRR with 12% monthly growth and 3% churn (net 9% growth), plus 2% expansion revenue (net 11%). Month 12 MRR: $69,740 ($837K ARR). Month 24: $243,230 ($2.9M ARR). Month 36: $848,370 ($10.2M ARR)." },
      { title: "Conservative Growth Model", description: "Starting at $50K MRR with 8% growth, 4% churn, and 1% expansion (net 5% monthly growth). Month 12: $89,780 ($1.08M ARR). Month 24: $161,190 ($1.93M ARR). A more realistic projection for a competitive market." },
    ],
  },
  "hiring-cost": {
    title: "Hiring Cost Calculator",
    keyword: "employee cost calculator startup",
    description: "The true cost of an employee is significantly more than their base salary. This calculator accounts for benefits, payroll taxes, equipment, and recruiting costs to show you the complete annual and monthly cost of each hire — critical information for burn rate planning.",
    whyItMatters: "Hiring is the single largest expense for most startups. Founders who only budget for salary consistently underestimate costs by 25–40%. Understanding the true cost per employee helps you make better hiring decisions and avoid the common mistake of hiring too fast after a fundraise.",
    component: HiringCostCalc,
    faqs: [
      { q: "How much more than salary does an employee really cost?", a: "Typically 25–40% above base salary, depending on location and benefits package. In the US, payroll taxes alone add 7.65% (employer's share of FICA). Health benefits can add $6,000–$20,000/year per employee. Equipment, software licenses, and recruiting costs add more on top." },
      { q: "Should I hire full-time employees or contractors?", a: "Contractors are cheaper upfront (no benefits, taxes, or equipment) and more flexible to scale up/down. But they're more expensive per hour, less committed, and you don't build institutional knowledge. Use contractors for specialized short-term work, and full-time hires for core team members who'll be with you for years." },
      { q: "How do I estimate recruiting costs?", a: "Internal recruiting costs $3,000–$8,000 per hire (job boards, hiring manager time, interviews). External recruiters charge 15–25% of first-year salary. For a $120K role, that's $18,000–$30,000. Engineer referral bonuses ($2,000–$10,000) are usually the most cost-effective sourcing method." },
    ],
    examples: [
      { title: "Junior Software Engineer", description: "Salary: $95,000. Benefits: $12,000. Payroll taxes: $7,268. Equipment: $3,000. Recruiting: $5,000. Total annual cost: $122,268. Monthly: $10,189. That's 28.7% above the base salary." },
      { title: "Senior Sales Hire", description: "Salary: $130,000 (base). Benefits: $15,000. Payroll taxes: $9,945. Equipment: $2,000. Recruiting (external): $32,500. Total first-year cost: $189,445. Monthly: $15,787. That's 45.7% above base, largely due to the recruiter fee." },
    ],
  },
  "ai-automation-roi": {
    title: "AI Automation ROI Calculator",
    keyword: "ai automation roi calculator",
    description: "AI automation is transforming how startups operate, but the ROI isn't always obvious. This calculator helps you model the financial impact of an AI automation initiative by comparing labor cost savings against implementation costs to determine ROI and payback period.",
    whyItMatters: "AI adoption is accelerating, but not every automation project delivers positive ROI. This calculator cuts through the hype and gives you hard numbers on whether a specific automation initiative makes financial sense — and how long it will take to pay for itself.",
    component: AiRoiCalc,
    faqs: [
      { q: "What's a good ROI for an AI automation project?", a: "A first-year ROI above 100% is strong (you earned back double your investment). Payback periods under 12 months are considered excellent. However, many AI projects also deliver non-financial benefits like faster processing, fewer errors, and 24/7 availability that aren't captured in simple ROI calculations." },
      { q: "What types of work are best suited for AI automation?", a: "Repetitive, rules-based tasks with high volume are the best candidates: data entry, document processing, customer support triage, report generation, invoice processing, and quality assurance. Tasks requiring deep creativity, complex judgment, or emotional intelligence are harder to automate effectively." },
      { q: "Should I replace employees or reallocate them?", a: "Reallocation is usually better. Moving employees from repetitive tasks to higher-value work (customer relationships, strategy, innovation) captures the cost savings of automation while retaining institutional knowledge and avoiding the disruption and morale impact of layoffs." },
    ],
    examples: [
      { title: "Customer Support Automation", description: "Automating Tier 1 support handles 3 FTE equivalents ($65K each = $195K/year). Automation costs $80K to implement. Annual savings: $195K. Net savings: $115K. ROI: 144%. Payback: 4.9 months." },
      { title: "Invoice Processing", description: "Replacing manual invoice processing that took 2 FTEs ($55K each = $110K/year). AI tool costs $45K/year. Annual savings: $110K - $45K = $65K. ROI: 144%. Plus fewer errors and faster processing." },
    ],
  },
  "equity-split": {
    title: "Founder Equity Split Calculator",
    keyword: "founder equity split calculator",
    description: "Splitting equity between co-founders is one of the most important — and most difficult — conversations in starting a company. This calculator helps you model a fair split based on each founder's contributions across four dimensions: capital invested, time commitment, intellectual property, and other contributions.",
    whyItMatters: "Getting the equity split wrong is a top reason co-founder relationships break down. An unfair split breeds resentment that festers over years. Having this conversation early, with a structured framework, prevents the ambiguity and assumptions that destroy partnerships. Use this calculator as a starting point for an honest discussion.",
    component: EquitySplitCalc,
    faqs: [
      { q: "Should co-founders always split equity 50/50?", a: "Not necessarily. Equal splits are simple and signal mutual respect, but they can be unfair if contributions are unequal. The split should reflect the value each founder brings — including the idea, technical skills, business development, capital, existing relationships, and ongoing time commitment. Whatever you decide, document it clearly." },
      { q: "When should co-founders discuss equity?", a: "As early as possible — ideally before any serious work begins. The longer you wait, the harder the conversation becomes. Each founder builds up assumptions about what they 'deserve,' and unspoken expectations lead to conflict. Have the conversation within the first month of working together." },
      { q: "What about vesting?", a: "Always implement vesting, regardless of the split. Standard vesting is 4 years with a 1-year cliff. This means no equity is earned until year 1 (the cliff), then it vests monthly or quarterly over the remaining 3 years. Vesting protects all founders: if someone leaves early, they don't walk away with a full share." },
      { q: "What if contributions change over time?", a: "Build in mechanisms for adjustment. Consider milestone-based equity (additional shares for hitting specific goals), performance vesting, or a formal review process at defined intervals. Many startups also reserve a portion of equity in an 'adjustment pool' to reward outsized contributions later." },
    ],
    examples: [
      { title: "Technical + Business Co-Founders", description: "Founder A (CEO): Capital 3, Time 8, IP 2, Other 5 = 18 points. Founder B (CTO): Capital 1, Time 9, IP 7, Other 3 = 20 points. Total: 38 points. Suggested split: A gets 47.4%, B gets 52.6%. Close enough to consider a 50/50 split with vesting." },
      { title: "Three Co-Founders with Capital", description: "Founder A: Capital 8, Time 7, IP 2, Other 3 = 20. Founder B: Capital 2, Time 9, IP 6, Other 2 = 19. Founder C: Capital 0, Time 9, IP 8, Other 1 = 18. Total: 57. Split: A 35.1%, B 33.3%, C 31.6%. Relatively even, reflecting balanced but different contributions." },
    ],
  },
};

// ── Page Component ──

export default function CalculatorPage() {
  const params = useParams<{ slug: string }>();
  const slug = params?.slug || "";
  const data = CALCULATOR_DATA[slug];
  const [showResults, setShowResults] = useState(false);
  const [disclaimerAccepted, setDisclaimerAccepted] = useState(false);
  const [showDisclaimer, setShowDisclaimer] = useState(false);

  useEffect(() => {
    const stored = sessionStorage.getItem("calc-disclaimer-accepted");
    if (stored === "true") setDisclaimerAccepted(true);
  }, []);

  useEffect(() => {
    setShowResults(false);
    window.scrollTo(0, 0);
  }, [slug]);

  const handleCalculate = () => {
    if (!disclaimerAccepted) {
      setShowDisclaimer(true);
    } else {
      setShowResults(true);
    }
  };

  const handleAcceptDisclaimer = () => {
    sessionStorage.setItem("calc-disclaimer-accepted", "true");
    setDisclaimerAccepted(true);
    setShowDisclaimer(false);
    setShowResults(true);
  };

  if (!data) {
    return (
      <>
        <Navbar />
        <main id="main-content" className="pt-20 min-h-screen flex items-center justify-center">
          <div className="text-center">
            <h1 className="text-2xl font-display font-bold mb-4">Calculator Not Found</h1>
            <Link href="/calculators" className="text-primary hover:underline">Back to all calculators</Link>
          </div>
        </main>
        <Footer />
      </>
    );
  }

  const CalcComponent = data.component;

  return (
    <>
      <Navbar />
      {showDisclaimer && <DisclaimerModal onAccept={handleAcceptDisclaimer} />}
      <main id="main-content" className="pt-20 min-h-screen">
        <section className="py-24 relative">
          <div className="absolute inset-0 bg-[linear-gradient(to_right,#80808012_1px,transparent_1px),linear-gradient(to_bottom,#80808012_1px,transparent_1px)] bg-[size:24px_24px]" />
          <div className="max-w-3xl mx-auto px-4 sm:px-6 relative z-10">
            <Link href="/calculators" className="inline-flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-8" data-testid="link-back-calculators">
              <ArrowLeft size={14} aria-hidden="true" />
              All Calculators
            </Link>

            <div className="mb-10">
              <div className="flex items-center gap-3 mb-4">
                <Calculator size={20} className="text-primary" aria-hidden="true" />
                <span className="text-primary font-medium tracking-wide uppercase text-sm">{data.keyword}</span>
              </div>
              <h1 className="text-2xl sm:text-3xl md:text-5xl font-display font-bold mb-6" data-testid="text-calculator-title">{data.title}</h1>
              <p className="text-lg text-muted-foreground leading-relaxed">{data.description}</p>
            </div>

            <Card className="border-white/5 p-6 mb-10" role="form" aria-label={`${data.title} inputs and results`}>
              <CalcComponent show={showResults} />
              <div className="mt-6">
                <Button onClick={handleCalculate} className="font-semibold w-full sm:w-auto" size="lg" data-testid="button-calculate">
                  <Calculator size={16} className="mr-2" aria-hidden="true" />
                  Calculate
                </Button>
              </div>
              <InlineDisclaimer />
            </Card>

            <div className="mb-16">
              <h2 className="text-2xl font-display font-bold mb-4">Why It Matters</h2>
              <p className="text-muted-foreground leading-relaxed">{data.whyItMatters}</p>
            </div>

            {data.examples.length > 0 && (
              <div className="mb-16">
                <h2 className="text-2xl font-display font-bold mb-6">Worked Examples</h2>
                <div className="grid gap-4">
                  {data.examples.map((ex, i) => (
                    <ExampleCard key={i} title={ex.title} description={ex.description} />
                  ))}
                </div>
              </div>
            )}

            <div className="mb-16">
              <h2 className="text-2xl font-display font-bold mb-6">Frequently Asked Questions</h2>
              <FAQ items={data.faqs} />
            </div>
          </div>
        </section>
      </main>
      <Footer />
    </>
  );
}
